import {
  createOrgRequestSchema,
  orgActivityQuerySchema,
  orgActivityResponseSchema,
  orgImagePresignRequestSchema,
  orgImagePresignResponseSchema,
  orgInsightsResponseSchema,
  orgPlanStatusSchema,
  planCatalogResponseSchema,
  planPurchaseHistoryResponseSchema,
  orgPlanPurchaseDetailSchema,
  planPurchaseQrResponseSchema,
  planPurchaseSyncResponseSchema,
  createPlanPurchaseResponseSchema,
  subscribeRequestSchema,
  buyTopupRequestSchema,
  redeemGiftCodeRequestSchema,
  redeemGiftCodeResponseSchema,
  orgPublicSchema,
  orgSchema,
  orgBranchSearchResponseSchema,
  orgSearchResponseSchema,
  updateOrgRequestSchema,
  type CreateOrgRequest,
  type OrgActivityQuery,
  type OrgImagePresignRequest,
  type OrgSearchQuery,
  type SubscribeRequest,
  type BuyTopupRequest,
  type RedeemGiftCodeRequest,
  type UpdateOrgRequest,
} from "@chotca/config";
import { z } from "zod";
import type { RequestContext } from "./http";
import { requestJson } from "./http";

const orgListResponseSchema = z.object({
  items: z.array(orgSchema),
});

export function createOrgsResource(ctx: RequestContext) {
  return {
    listMine() {
      return requestJson(ctx, {
        method: "GET",
        path: "/me/orgs",
        dataSchema: orgListResponseSchema,
        auth: true,
      });
    },

    create(body: CreateOrgRequest) {
      const payload = createOrgRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: "/orgs",
        body: payload,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    get(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}`,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    // Worker-facing public profile. Use this from the worker app —
    // membership is NOT required and the response strips contact phone /
    // billing fields.
    getPublic(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/public`,
        dataSchema: orgPublicSchema,
        auth: true,
      });
    },

    // Worker-side picker. Returns orgs with at least 1 OPEN posting.
    search(query: OrgSearchQuery = {}) {
      const params: Record<string, string | number> = {};
      if (query.q) params.q = query.q;
      if (query.cursor) params.cursor = query.cursor;
      if (query.limit) params.limit = query.limit;
      return requestJson(ctx, {
        method: "GET",
        path: "/orgs/search",
        query: params,
        dataSchema: orgSearchResponseSchema,
        auth: true,
      });
    },

    // Worker experience picker. Searches all non-deleted orgs (no
    // OPEN-posting filter) so a worker can link a past job to an org
    // that has no current ads.
    searchForExperience(query: OrgSearchQuery = {}) {
      const params: Record<string, string | number> = {};
      if (query.q) params.q = query.q;
      if (query.cursor) params.cursor = query.cursor;
      if (query.limit) params.limit = query.limit;
      return requestJson(ctx, {
        method: "GET",
        path: "/orgs/experience-search",
        query: params,
        dataSchema: orgBranchSearchResponseSchema,
        auth: true,
      });
    },

    // Weekly stats + credit balance + tips for the employer postings page.
    getInsights(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/insights`,
        dataSchema: orgInsightsResponseSchema,
        auth: true,
      });
    },

    // Subscription plan status (effective plan, remaining sourcing/boost, days
    // left) for the employer "gói" screen.
    getPlanStatus(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/plan`,
        dataSchema: orgPlanStatusSchema,
        auth: true,
      });
    },

    // Buyable plan catalog (active, non-FREE plans with live prices + optional
    // struck-through originalPriceVnd) for the upgrade modal.
    getPlanCatalog(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/plan/catalog`,
        dataSchema: planCatalogResponseSchema,
        auth: true,
      });
    },

    // Purchase history (every order, any status) for the billing screen.
    listPlanPurchases(orgId: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/plan/purchases`,
        dataSchema: planPurchaseHistoryResponseSchema,
        auth: true,
      });
    },

    // One order's full detail for the invoice/result screen (OWNER). Works for
    // any status. `orderCode` is the PLAN-<seq> providerOrderId.
    getPlanPurchase(orgId: string, orderCode: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/plan/purchases/${orderCode}`,
        dataSchema: orgPlanPurchaseDetailSchema,
        auth: true,
      });
    },

    // Start a plan purchase (OWNER). Returns the PENDING order + (prod) a Sepay
    // checkout handoff; on dev `simulated` is true → confirm to settle.
    subscribePlan(orgId: string, body: SubscribeRequest) {
      const payload = subscribeRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/plan/subscribe`,
        body: payload,
        dataSchema: createPlanPurchaseResponseSchema,
        auth: true,
      });
    },

    // Redeem a plan gift code ("mã quà tặng") for the org (OWNER). Free grant —
    // returns the org's new plan status + what was granted.
    redeemGiftCode(orgId: string, body: RedeemGiftCodeRequest) {
      const payload = redeemGiftCodeRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/plan/redeem`,
        body: payload,
        dataSchema: redeemGiftCodeResponseSchema,
        auth: true,
      });
    },

    // Buy an à-la-carte top-up (extra sourcing/boost). OWNER, Pro+. Same
    // PENDING order + checkout handoff shape as subscribePlan.
    buyTopup(orgId: string, body: BuyTopupRequest) {
      const payload = buyTopupRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/plan/topup`,
        body: payload,
        dataSchema: createPlanPurchaseResponseSchema,
        auth: true,
      });
    },

    // VietQR bank-transfer QR for a PENDING plan/topup order (OWNER). `orderCode`
    // is the PLAN-<seq> providerOrderId. Customer scans it → Casso webhook settles.
    getPlanPurchaseQr(orgId: string, orderCode: string) {
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/plan/purchases/${orderCode}/qr`,
        dataSchema: planPurchaseQrResponseSchema,
        auth: true,
      });
    },

    // Nudge Casso to sync after the buyer confirms payment (OWNER). Best-effort;
    // the order still settles via the webhook. Returns { synced }.
    syncPlanPurchase(orgId: string, orderCode: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/plan/purchases/${orderCode}/sync`,
        dataSchema: planPurchaseSyncResponseSchema,
        auth: true,
      });
    },

    // Settle a simulated plan/topup purchase (dev only). Returns plan status.
    confirmPlanPurchase(orgId: string, purchaseId: string) {
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/plan/purchases/${purchaseId}/confirm`,
        dataSchema: orgPlanStatusSchema,
        auth: true,
      });
    },

    // Org-scoped audit feed (OWNER + HR only).
    listActivity(orgId: string, query: OrgActivityQuery = {}) {
      const q = orgActivityQuerySchema.parse(query);
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(q)) {
        if (v === undefined || v === null) continue;
        params.set(k, String(v));
      }
      const qs = params.toString();
      return requestJson(ctx, {
        method: "GET",
        path: `/orgs/${orgId}/activity${qs ? `?${qs}` : ""}`,
        dataSchema: orgActivityResponseSchema,
        auth: true,
      });
    },

    update(orgId: string, body: UpdateOrgRequest) {
      const payload = updateOrgRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "PATCH",
        path: `/orgs/${orgId}`,
        body: payload,
        dataSchema: orgSchema,
        auth: true,
      });
    },

    // Presign one gallery image upload (OWNER only). PUT the file to the
    // returned URL, then persist the key via `update({ galleryImageKeys })`.
    presignImage(orgId: string, body: OrgImagePresignRequest) {
      const payload = orgImagePresignRequestSchema.parse(body);
      return requestJson(ctx, {
        method: "POST",
        path: `/orgs/${orgId}/images/presign`,
        body: payload,
        dataSchema: orgImagePresignResponseSchema,
        auth: true,
      });
    },
  };
}
